import { Badge, Group, Paper, Stack, Text } from "@mantine/core";
import { IconClipboardText } from "@tabler/icons-react";
import type { FileOutput } from "@/api-types";
import type { EngagementSurveyStatus, SurveyStatus } from "@/utils/survey";
import { FancyInlineLoader } from "./FancyLoader";
import { LibraryItem } from "./LibraryItem";

export interface SurveyListProps {
  files: FileOutput[];
  surveyStatuses?: Record<string, EngagementSurveyStatus>;
  onSurveyClick: (fileId: string, status: SurveyStatus) => void;
  isLoading?: boolean;
  onDelete?: (fileId: string) => void;
}

const getStatusColor = (status?: SurveyStatus) => {
  switch (status) {
    case "FILLED":
      return "green";
    case "PARTIALLY_FILLED":
      return "orange";
    case "NOT_FILLED":
    case undefined:
      return "gray";
    default:
      return "blue";
  }
};

const getStatusLabel = (status?: SurveyStatus) => {
  switch (status) {
    case "FILLED":
      return "Completed";
    case "PARTIALLY_FILLED":
      return "In Progress";
    case "NOT_FILLED":
    case undefined:
      return "Not Started";
    default:
      return "Unknown";
  }
};

export const SurveyList = ({
  files,
  surveyStatuses = {},
  onSurveyClick,
  isLoading = false,
  onDelete,
}: SurveyListProps) => {
  // Filter is done at the parent component level now
  const surveyFiles = files.filter((file) => file.type === "SURVEY");

  if (isLoading) {
    return (
      <Paper p="lg" withBorder>
        <Stack align="center" gap="md">
          <FancyInlineLoader />
          <Text size="sm" c="dimmed">
            Loading surveys...
          </Text>
        </Stack>
      </Paper>
    );
  }

  if (surveyFiles.length === 0) {
    return (
      <Paper p="lg" withBorder>
        <Stack align="center" gap="md">
          <IconClipboardText size={48} opacity={0.5} />
          <Text c="dimmed">No surveys available for this session.</Text>
        </Stack>
      </Paper>
    );
  }

  return (
    <Stack gap="md">
      {surveyFiles.map((file) => {
        const engagementSurveyStatus = surveyStatuses[file.id] ?? {
          status: "NOT_FILLED",
        };
        const status = engagementSurveyStatus.status;

        return (
          <LibraryItem
            key={file.id}
            kind="file"
            item={file}
            onFileClick={(item) => {
              onSurveyClick(item.id, status);
            }}
            readonly={true}
            onDelete={onDelete}
          >
            <Group>
              <Badge color={getStatusColor(status)}>
                {getStatusLabel(status)}
              </Badge>
            </Group>
          </LibraryItem>
        );
      })}
    </Stack>
  );
};
